import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
import { DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * A custom element that displays a cart icon with a live item-count bubble.
 *
 * Responsibilities:
 * - Sync bubble count on cartLinesUpdate events (real-time add/remove)
 * - Sync bubble count when the cart drawer closes (KYS UX-002)
 * - Sync bubble count on mount and bfcache restore (ARI-006)
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble       - The cart bubble wrapper element.
 * @property {HTMLElement} cartBubbleText   - The animated text element inside the bubble.
 * @property {HTMLElement} cartBubbleCount  - The element that displays the numeric count.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @returns {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  /** @param {number} value */
  set currentCartCount(value) {
    // Cap display at 99; above that the bubble shows blank (overflow implied).
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    window.addEventListener('pageshow', this.#onPageShow);
    // ARI-006: Sync on mount in case DOM morphing hasn't run yet.
    this.#syncFromServer(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * KYS UX-002: Single navbar sync pass when the cart drawer closes.
   * Ensures the bubble reflects the final cart state after any in-drawer edits.
   * @param {Event} event
   */
  #onDrawerClose = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncFromServer(false);
  };

  /**
   * Re-sync on bfcache restore (back/forward navigation from cache).
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#syncFromServer(false);
  };

  /**
   * Handles real-time cart line updates dispatched by add-to-cart / remove flows.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount =
          typeof detail?.itemCount === 'number'
            ? detail.itemCount
            : (cart?.itemCount ?? cart?.totalQuantity ?? 0);
        this.#renderBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] cartLinesUpdate promise rejected:', error);
      });
  };

  /**
   * ARI-006: Fetch canonical cart state from the server and sync the bubble.
   * Used on mount, bfcache restore, and drawer close — covers cases where
   * Section Rendering API morphing hasn't fired or is unavailable.
   * @param {boolean} [animate=false]
   */
  #syncFromServer = (animate = false) => {
    fetch(`${Theme.routes.cart_url}.js`)
      .then((res) => res.json())
      .then((cart) => {
        if (typeof cart?.item_count === 'number') {
          this.#renderBubble(cart.item_count, animate);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Server sync failed:', error);
      });
  };

  /**
   * Updates the bubble visibility, count, host class, sessionStorage cache,
   * and optionally plays the pop animation.
   * @param {number} itemCount
   * @param {boolean} [animate=true]
   */
  #renderBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.currentCartCount = itemCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() })
    );

    if (!animate || itemCount === 0) return;

    // Yield to the browser so visibility changes are painted before animating.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  // Public aliases retained for any external callers referencing these names.
  /** @param {number} itemCount @param {boolean} [animate] */
  renderCartBubble = (itemCount, animate) => this.#renderBubble(itemCount, animate);
  ensureCartBubbleIsCorrect = () => this.#syncFromServer(false);
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
